import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Camera, Upload, Download, RefreshCw, Sparkles, CheckCircle2,
  Star, Zap, BarChart3, Lightbulb, ChevronDown, X, Loader2,
} from "lucide-react";
import SEO from "../components/SEO";

const BACKGROUNDS = [
  { key: "white",          label: "Pure White",       preview: "#FFFFFF", dark: false },
  { key: "studio_gray",    label: "Studio Gray",      preview: "#C3C3C3", dark: false },
  { key: "light_gray",     label: "Light Gray",       preview: "linear-gradient(135deg,#E1E1E1,#AAAAAA)", dark: false },
  { key: "navy",           label: "Navy",             preview: "#122C5A", dark: true  },
  { key: "corporate_blue", label: "Corporate Blue",   preview: "linear-gradient(135deg,#26589E,#0E2658)", dark: true  },
  { key: "dark",           label: "Dark Studio",      preview: "linear-gradient(135deg,#1C1C2D,#080812)", dark: true  },
  { key: "beige",          label: "Warm Beige",       preview: "#EEE2D2", dark: false },
  { key: "teal",           label: "Executive Teal",   preview: "linear-gradient(135deg,#125864,#05343E)", dark: true  },
];

const DIMENSIONS = [
  { key: "lighting",   label: "Lighting Quality" },
  { key: "framing",    label: "Framing & Composition" },
  { key: "expression", label: "Expression & Confidence" },
  { key: "attire",     label: "Professional Attire" },
  { key: "background", label: "Background Quality" },
];

const GRADE_COLOR = { "A+": "#D3FF24", A: "#D3FF24", "B+": "#7BE3A0", B: "#7BE3A0", "C+": "#FACC15", C: "#FACC15", D: "#F87171", F: "#EF4444" };

function UploadZone({ onFile }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const handle = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image must be under 20 MB"); return; }
    onFile(file);
  }, [onFile]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDrag(false);
    handle(e.dataTransfer.files[0]);
  }, [handle]);

  return (
    <div
      className={`border-2 border-dashed rounded-none transition-all cursor-pointer flex flex-col items-center justify-center gap-4 py-14 px-8 ${drag ? "border-[#D3FF24] bg-[#D3FF24]/5" : "border-white/20 hover:border-white/40 bg-white/[0.02]"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files[0])} />
      <div className="w-16 h-16 rounded-full bg-[#D3FF24]/10 border border-[#D3FF24]/30 flex items-center justify-center">
        <Camera className="w-7 h-7 text-[#D3FF24]" strokeWidth={1.5} />
      </div>
      <div className="text-center">
        <p className="font-semibold text-white mb-1">Drop your photo here</p>
        <p className="text-sm text-neutral-400">or click to browse — JPG, PNG, WEBP up to 20 MB</p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center mt-2">
        {["Clear face visible", "Good lighting", "Front-facing", "High resolution"].map((t) => (
          <span key={t} className="flex items-center gap-1 text-[10px] text-neutral-500 uppercase tracking-widest">
            <CheckCircle2 className="w-3 h-3 text-[#D3FF24]" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function ScoreBar({ value, max = 20 }) {
  const pct = Math.round((value / max) * 100);
  const color = pct >= 80 ? "#D3FF24" : pct >= 60 ? "#7BE3A0" : pct >= 40 ? "#FACC15" : "#F87171";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-neutral-400 w-8 text-right">{value}/20</span>
    </div>
  );
}

function AnalysisCard({ analysis }) {
  const [open, setOpen] = useState(true);
  if (!analysis) return null;
  const gradeCol = GRADE_COLOR[analysis.grade] || "#D3FF24";

  return (
    <div className="border border-white/10 bg-[#0F0F0F] mt-6">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02]"
      >
        <div className="flex items-center gap-3">
          <BarChart3 className="w-4 h-4 text-[#D3FF24]" />
          <span className="font-semibold text-sm">AI Photo Analysis</span>
          <span className="text-xs px-2 py-0.5 rounded-sm font-bold" style={{ color: gradeCol, border: `1px solid ${gradeCol}40`, background: `${gradeCol}10` }}>
            Grade {analysis.grade}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-[#D3FF24] fill-[#D3FF24]" />
            <span className="font-bold text-lg" style={{ color: gradeCol }}>{analysis.score}</span>
            <span className="text-neutral-500 text-xs">/100</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-5 border-t border-white/10 pt-5">
          <div className="grid grid-cols-2 gap-4">
            {DIMENSIONS.map((d) => (
              <div key={d.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-neutral-400">{d.label}</span>
                </div>
                <ScoreBar value={analysis[d.key] ?? 0} />
              </div>
            ))}
          </div>

          {analysis.top_tips?.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-3.5 h-3.5 text-[#D3FF24]" />
                <span className="text-xs uppercase tracking-widest font-semibold text-neutral-400">Top Tips</span>
              </div>
              <ul className="space-y-2">
                {analysis.top_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                    <span className="text-[#D3FF24] font-bold mt-0.5 shrink-0">{i + 1}.</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 py-2 px-3 bg-white/[0.03] border border-white/10">
            {analysis.linkedin_ready
              ? <CheckCircle2 className="w-4 h-4 text-[#D3FF24] shrink-0" />
              : <X className="w-4 h-4 text-[#F87171] shrink-0" />}
            <span className="text-xs text-neutral-400">
              {analysis.linkedin_ready
                ? "LinkedIn-ready — this headshot meets professional standards"
                : "Not yet LinkedIn-ready — follow the tips above to improve"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function BeforeAfter({ original, result }) {
  const [showBefore, setShowBefore] = useState(false);
  return (
    <div className="relative group overflow-hidden border border-white/10">
      <img
        src={showBefore ? original : result}
        alt={showBefore ? "Original" : "Professional headshot"}
        className="w-full object-cover"
        style={{ aspectRatio: "3/4", objectPosition: "top" }}
      />
      <div className="absolute bottom-0 left-0 right-0 flex">
        <button
          onMouseEnter={() => setShowBefore(true)}
          onMouseLeave={() => setShowBefore(false)}
          className="flex-1 py-2 text-[10px] uppercase tracking-widest font-semibold transition-all bg-black/70 hover:bg-black/90 text-neutral-400 hover:text-white"
        >
          {showBefore ? "← Original" : "Hold to compare"}
        </button>
      </div>
      <div className="absolute top-3 right-3">
        <span className={`text-[10px] px-2 py-1 font-semibold uppercase tracking-widest ${showBefore ? "bg-white/20 text-white" : "bg-[#D3FF24] text-black"}`}>
          {showBefore ? "Before" : "After"}
        </span>
      </div>
    </div>
  );
}

export default function HeadshotGenerator() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bg, setBg] = useState("studio_gray");
  const [enhance, setEnhance] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { image, analysis }

  const handleFile = useCallback((f) => {
    setFile(f);
    setResult(null);
    const url = URL.createObjectURL(f);
    setPreview(url);
  }, []);

  const generate = async () => {
    if (!file) { toast.error("Please upload a photo first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("background", bg);
      fd.append("enhance", enhance ? "true" : "false");

      const res = await fetch("/api/headshot/generate", {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Generation failed");
      }
      const data = await res.json();
      setResult(data);
      toast.success("Headshot generated!");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.image) return;
    const a = document.createElement("a");
    a.href = result.image;
    a.download = "wingman-headshot.jpg";
    a.click();
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="px-10 py-10 max-w-5xl">
      <SEO title="Professional Headshot Generator" description="Transform any photo into a studio-quality professional headshot with AI background removal, lighting enhancement, and expert analysis." path="/app/headshot" />

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Camera className="w-6 h-6 text-[#D3FF24]" strokeWidth={1.5} />
            <h1 className="font-display text-5xl font-black tracking-tighter">Headshot Generator</h1>
          </div>
          <p className="text-neutral-500 ml-9">Studio-quality professional headshots in seconds — powered by AI.</p>
        </div>
        <div className="flex items-center gap-2 border border-[#D3FF24]/30 bg-[#D3FF24]/5 px-3 py-1.5">
          <Zap className="w-3.5 h-3.5 text-[#D3FF24]" />
          <span className="text-xs font-semibold text-[#D3FF24] uppercase tracking-widest">AI-Powered</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        {/* Left column */}
        <div className="space-y-6">
          {/* Upload / source photo */}
          <div className="border border-white/10 bg-[#0A0A0A]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-[#D3FF24]" />
                <span className="font-semibold text-sm">Source Photo</span>
              </div>
              {file && (
                <button onClick={reset} className="text-xs text-neutral-500 hover:text-[#F87171] transition flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
            <div className="p-6">
              {!file
                ? <UploadZone onFile={handleFile} />
                : (
                  <div className="flex items-start gap-4">
                    <img src={preview} alt="Source" className="w-24 h-32 object-cover object-top border border-white/10 shrink-0" />
                    <div>
                      <p className="font-medium text-sm text-white mb-1">{file.name}</p>
                      <p className="text-xs text-neutral-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      <button
                        onClick={() => document.getElementById("reupload-input").click()}
                        className="mt-3 text-xs text-[#D3FF24] hover:underline flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" /> Change photo
                      </button>
                      <input id="reupload-input" type="file" accept="image/*" className="hidden"
                        onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Background */}
          <div className="border border-white/10 bg-[#0A0A0A]">
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#D3FF24]" />
              <span className="font-semibold text-sm">Background Style</span>
            </div>
            <div className="p-6 grid grid-cols-4 gap-3">
              {BACKGROUNDS.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBg(b.key)}
                  className={`group flex flex-col items-center gap-2 transition ${bg === b.key ? "opacity-100" : "opacity-60 hover:opacity-90"}`}
                >
                  <div
                    className="w-full aspect-square border-2 transition-all"
                    style={{
                      background: b.preview,
                      borderColor: bg === b.key ? "#D3FF24" : "transparent",
                      boxShadow: bg === b.key ? "0 0 0 1px #D3FF24" : "none",
                    }}
                  />
                  <span className={`text-[9px] uppercase tracking-widest font-semibold ${bg === b.key ? "text-[#D3FF24]" : "text-neutral-500"}`}>
                    {b.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Enhancement */}
          <div className="border border-white/10 bg-[#0A0A0A] px-6 py-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <Sparkles className="w-4 h-4 text-[#D3FF24]" />
                <span className="font-semibold text-sm">AI Enhancement</span>
              </div>
              <p className="text-xs text-neutral-500 ml-6">Auto-improve contrast, sharpness, and color tone</p>
            </div>
            <button
              onClick={() => setEnhance((v) => !v)}
              className={`relative w-12 h-6 rounded-full border transition-all ${enhance ? "bg-[#D3FF24] border-[#D3FF24]" : "bg-white/10 border-white/20"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all ${enhance ? "left-6" : "left-0.5"}`} />
            </button>
          </div>

          {/* Generate button */}
          <button
            onClick={generate}
            disabled={!file || loading}
            className="w-full bg-[#D3FF24] text-black py-4 text-sm uppercase tracking-widest font-bold hover:brightness-105 transition disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing photo…</>
              : <><Camera className="w-4 h-4" /> Generate Professional Headshot</>}
          </button>

          {loading && (
            <div className="border border-white/10 bg-[#0A0A0A] px-6 py-4">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-4 h-4 text-[#D3FF24] animate-spin" />
                <span className="text-sm font-medium">Generating your headshot…</span>
              </div>
              <div className="space-y-1.5 text-xs text-neutral-500">
                {["Removing background with AI", "Compositing professional background", "Applying enhancements", "Analyzing photo quality"].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#D3FF24]/60" /> {s}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column — result */}
        <div className="space-y-4">
          {!result && !loading && (
            <div className="border border-white/10 bg-[#0A0A0A] flex flex-col items-center justify-center py-20 px-8 text-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[#D3FF24]/5 border border-[#D3FF24]/20 flex items-center justify-center">
                <Camera className="w-8 h-8 text-[#D3FF24]/40" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-neutral-300 mb-1">Your headshot appears here</p>
                <p className="text-xs text-neutral-600">Upload a photo and click generate</p>
              </div>
              <div className="mt-4 space-y-2 text-left w-full">
                {["AI background removal", "Professional background compositing", "Lighting & color enhancement", "LinkedIn readiness score"].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-neutral-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24]/60 shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result && (
            <>
              <BeforeAfter original={preview} result={result.image} />
              <button
                onClick={download}
                className="w-full flex items-center justify-center gap-2 py-3 border border-[#D3FF24] text-[#D3FF24] text-xs uppercase tracking-widest font-semibold hover:bg-[#D3FF24] hover:text-black transition"
              >
                <Download className="w-4 h-4" /> Download Headshot
              </button>
              <AnalysisCard analysis={result.analysis} />
            </>
          )}

          {/* Tips panel */}
          <div className="border border-white/10 bg-[#0A0A0A] p-5">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-3">Best Results</p>
            <ul className="space-y-2">
              {[
                "Use a front-facing photo with your face clearly visible",
                "Ensure even, diffused lighting with no harsh shadows",
                "Business casual or formal attire works best",
                "Minimum 800×800px — higher resolution = better output",
                "Neutral expression or a gentle, confident smile",
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-xs text-neutral-500">
                  <span className="text-[#D3FF24] shrink-0 mt-0.5">→</span> {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
